import type { AppContext } from '@application/context/AppContext';
import { RemoteAuthService } from '@application/auth/RemoteAuthService';
import { isHybridDeployment, isRemoteMultiStationMode } from '@infra/licence/DataModeResolver';
import { getBusinessApiClient } from '@infra/api/BusinessApiClient';
import { SecureLicenceStore } from '@infra/licence/SecureLicenceStore';
import { getHybridSyncRuntime } from '@application/sync/HybridSyncBootstrap';
import { isBrowserCloudHost } from '@application/licence/CloudWebHost';
import { purgeCloudBrowserTenantData } from '@infra/persistence/cloudBrowserReset';

/** Efface sessions utilisateur locales/distantes et jeton d'installation (après activation). */
export async function clearAuthStateForActivation(_ctx: AppContext): Promise<void> {
  _ctx.auth.logout();
  getBusinessApiClient().setUserToken(null);
  await SecureLicenceStore.clearInstallationSession();
}

/** Déconnexion locale ou distante selon le mode de persistance verrouillé. */
export async function performAuthLogout(ctx: AppContext): Promise<void> {
  const { endTrialUsageSession } = await import('@infra/legal/TrialUsageStore');
  await endTrialUsageSession();
  // Synchronisation Hybride : le moteur s'arrête avec la session ; la file d'envoi reste intacte.
  getHybridSyncRuntime().deactivate();
  ctx.auth.logout();
  getBusinessApiClient().setUserToken(null);

  const hybrid = isHybridDeployment(ctx.getLicenceRuntime?.()?.getState()?.claims ?? null);
  if (!isRemoteMultiStationMode(ctx.getLockedDataMode()) && !hybrid) return;

  try {
    await new RemoteAuthService().logout();
  } catch {
    // Jetons locaux déjà effacés — l'API peut être injoignable.
  }
  // Navigateur Cloud : rien de l'entreprise ne doit survivre à la fermeture de session (poste partagé, bascule A -> B).
  if (isBrowserCloudHost()) await purgeCloudBrowserTenantData();
}
